import React from 'react';
import { useTranslation } from 'react-i18next';
import { HomeTransferStatusType } from 'api/transfer/types';
import {
  TransferProgressStep,
  TransferProgressStepStatus,
} from '../TransferProgressStep/TransferProgressStep';
import { SectionTitle, StepperWrapper } from './TransferProgress.styles';
import { STEPS_BY_WORKFLOW, getWorkflowType, getActiveIndex } from './transfer-progress-steps';
import {
  TranslateFn,
  getStepBullets,
  getStepDescription,
  getStepLabel,
} from './transfer-progress-content';

type Props = {
  status: HomeTransferStatusType;
};

function getStepStatus(stepIndex: number, activeIndex: number): TransferProgressStepStatus {
  if (stepIndex < activeIndex) return 'complete';
  if (stepIndex === activeIndex) return 'active';
  return 'not started';
}

export function TransferProgress({ status }: Props) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'support&FAQs.faqCategories.agreementTransferStatus',
  });

  const workflow = getWorkflowType(status);
  const allSteps = STEPS_BY_WORKFLOW[workflow];
  const activeIndex = getActiveIndex(allSteps, status, workflow === 'standard' ? 1 : 0);

  const derivedSteps = allSteps.map((step, i) => {
    const stepStatus = getStepStatus(i, activeIndex);
    const subState =
      stepStatus === 'active' && step.subStateMilestone && status[step.subStateMilestone.field]
        ? step.subStateMilestone.name
        : undefined;
    return { ...step, subState, stepStatus };
  });

  // The UCC fee step is hidden until payment is complete, because it's shown in
  //   required tasks until then.
  const visibleSteps = derivedSteps.filter(
    (step) => !step.hideUntilCompleted || step.hideUntilCompleted(status),
  );

  // i18next's `t` has a broad overloaded signature; narrow to the simpler
  // (key, defaultValue?) => string shape that the helper functions expect.
  const tFn = t as TranslateFn;

  return (
    <div>
      <SectionTitle>{t('requiredTasks.progressTitle')}</SectionTitle>
      <StepperWrapper>
        {visibleSteps.map((step, visibleIndex) => {
          const label = getStepLabel(tFn, step.key, step.stepStatus);
          const description = getStepDescription(tFn, step.key, step.subState, step.stepStatus);
          const bullets = getStepBullets(tFn, step, step.subState, step.stepStatus);
          const nextStepStatus = visibleSteps[visibleIndex + 1]?.stepStatus ?? null;

          return (
            <TransferProgressStep
              key={step.key}
              status={step.stepStatus}
              title={label}
              statusText={description ?? undefined}
              bullets={bullets}
              hasPrev={visibleIndex > 0}
              nextStatus={nextStepStatus}
            />
          );
        })}
      </StepperWrapper>
    </div>
  );
}

export default TransferProgress;
